<template>
  <div class="puq-page">
    <UserAppChrome back-mode="history" :show-dock="false" />
    <div class="puq-card" ref="formRootRef">
      <div class="puq-stepper">
        <button class="puq-stepper__back" type="button" @click="goBack" aria-label="Torna indietro">
          <span aria-hidden="true">‹</span>
        </button>
        <div class="puq-stepper__text">
          <p class="puq-stepper__caption">{{ isManualStandalone ? 'Check manuale' : 'Check finale' }}</p>
          <h1 ref="stepHeadingRef" tabindex="-1" class="puq-stepper__title">{{ currentSection.title }}</h1>
          <p class="puq-stepper__subtitle">
            Sezione {{ activeSectionIndex + 1 }} di {{ sectionFlow.length }}
          </p>
        </div>
      </div>

      <div class="puq-progress" role="progressbar" aria-label="Avanzamento del check finale" :aria-valuenow="activeSectionIndex + 1" :aria-valuemax="sectionFlow.length" :aria-valuemin="0" :aria-valuetext="`Sezione ${activeSectionIndex + 1} di ${sectionFlow.length}`">
        <div class="puq-progress__track">
          <span class="puq-progress__fill" :style="{ width: progressValue + '%' }"></span>
        </div>
        <span class="puq-progress__label">{{ progressValue }}%</span>
      </div>

      <details class="puq-meta-card">
        <summary>Informazioni e PDF <span aria-hidden="true">⌄</span></summary>
        <div class="puq-meta-content">
        <div class="puq-meta-info">
          <span class="meta-label">Ultimo aggiornamento</span>
          <span class="meta-value">
            <template v-if="questionarioInfo.updatedAt">
              {{ formatDate(questionarioInfo.updatedAt) }}
            </template>
            <template v-else>Nessun dato salvato</template>
          </span>
        </div>
        <div class="puq-meta-actions">
          <button
            type="button"
            class="meta-btn"
            @click="exportFormAsPdf"
            :disabled="pdfExporting || loading || !!errorMessage || !!linkMismatchWarning"
            :aria-busy="pdfExporting"
          >
            <span v-if="pdfExporting" class="meta-btn__spinner" aria-hidden="true"></span>
            <span>{{ pdfExporting ? 'Esporto PDF…' : 'Esporta PDF' }}</span>
          </button>
        </div>
        </div>
      </details>
      <p
        v-if="allowPartialSubmission"
        class="muted"
        style="margin: 0 0 16px;"
      >
        Questo check è stato reso parziale: puoi inviare i dati anche senza foto e misurazioni.
      </p>

      <transition name="fade">
        <div
          v-if="actionMessage"
          ref="actionMessageRef"
          tabindex="-1"
          :class="['puq-banner', actionMessageType === 'error' ? 'puq-banner--error' : 'puq-banner--success']"
          :role="actionMessageType === 'error' ? 'alert' : 'status'"
        >
          {{ actionMessage }}
        </div>
      </transition>

      <div
        v-if="linkMismatchWarning"
        class="puq-banner puq-banner--error puq-banner--login"
        role="alert"
      >
        <span>{{ linkMismatchWarning }}</span>
        <router-link class="puq-banner__cta" :to="loginRoute">Vai al login</router-link>
      </div>

    <div v-if="loading" class="puq-state">Caricamento check finale…</div>
    <div v-else-if="errorMessage" class="puq-state puq-state--error">{{ errorMessage }}</div>
    <div v-else-if="linkMismatchWarning" class="puq-state">Accedi per visualizzare il check finale.</div>
    <div v-else-if="lockWarning" class="puq-state puq-state--error">{{ lockWarning }}</div>
    <div
      v-else-if="isAdminApproved"
      class="puq-state puq-state--error"
    >
      Check già approvato
    </div>
    <div
      v-else-if="isHardLocked"
      class="puq-state puq-state--error"
    >
      Check già inviato: contatta il supporto per eventuali modifiche.
    </div>
    <template v-else>
      <!-- Final submission uses the complete schema; hidden sections must not trap native validation. -->
      <form class="qi-form" novalidate @submit.prevent="handleFormSubmit">
              <div v-if="missingMandatoryFields.length" ref="requiredSummaryRef" tabindex="-1" class="puq-banner puq-banner--error puq-required-banner" role="alert">
                <p class="puq-required-banner__title">Completa questi campi prima di inviare. Tocca un campo per raggiungerlo:</p>
                <ul class="puq-required-banner__list"><li v-for="field in missingMandatoryFields" :key="field.id"><button type="button" class="puq-field-link" @click="goToMissingField(field.id)">{{ field.label }}</button></li></ul>
              </div>
              <!-- Allenamento -->
              <section
                :class="['qi-block', { 'qi-block--active': isSectionActive('allenamento') }]"
                v-show="isSectionActive('allenamento')"
              >
                <header class="qi-block__header">
                  <h2 class="qi-block__title">Allenamento</h2>
                </header>
                <div class="qi-block__body">
                  <div class="grid two">
                    <label>
                      <span>
                        Quante sedute hai svolto in media a settimana?
                        <span v-if="isFieldRequired('seduteSettimanali')" data-field="seduteSettimanali" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <select v-model="form.seduteSettimanali">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in SEDUTE_SETTIMANALI_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>
                        Quanto hai rispettato l'allenamento da 1 a 100?
                        <span v-if="isFieldRequired('adesioneAllenamento')" data-field="adesioneAllenamento" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <select v-model="form.adesioneAllenamento">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in ADESIONE_PERCENT_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                  </div>
                  <label>
                    <span>
                      Hai affiancato altre attività “sportive” ai nostri allenamenti? Scrivici qui quali e quante volte a settimana…
                      <span v-if="isFieldRequired('attivitaAffiancate')" data-field="attivitaAffiancate" class="required-asterisk" aria-hidden="true">*</span>
                    </span>
                    <textarea v-model.trim="form.attivitaAffiancate" rows="3"></textarea>
                  </label>
                  <label>
                    <span>
                      Scrivi qui eventuali note riguardanti l’allenamento, più info ci dai e più potremo darti supporto…
                      <span v-if="isFieldRequired('noteAllenamento')" data-field="noteAllenamento" class="required-asterisk" aria-hidden="true">*</span>
                    </span>
                    <textarea v-model.trim="form.noteAllenamento" rows="3"></textarea>
                  </label>
                </div>
              </section>

              <!-- Alimentazione -->
              <section
                :class="['qi-block', { 'qi-block--active': isSectionActive('alimentazione') }]"
                v-show="isSectionActive('alimentazione')"
              >
                <header class="qi-block__header">
                  <h2 class="qi-block__title">Alimentazione</h2>
                </header>
                <div class="qi-block__body">
                  <div class="grid two">
                    <label>
                      <span>
                        Hai rispettato la strategia alimentare (ciclizzazione dei carboidrati)?
                        <span v-if="isFieldRequired('strategiaAlimentare')" data-field="strategiaAlimentare" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <select v-model="form.strategiaAlimentare">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in STRATEGIA_ALIMENTARE_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>
                        Hai rispettato le grammature assegnate?
                        <span v-if="isFieldRequired('rispettoGrammature')" data-field="rispettoGrammature" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <select v-model="form.rispettoGrammature">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in RISPETTO_GRAMMATURE_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                  </div>

                  <div class="grid two">
                    <label>
                      <span>
                        Quanti pasti liberi hai fatto in questo programma?
                        <span v-if="isFieldRequired('pastiLiberi')" data-field="pastiLiberi" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <select v-model="form.pastiLiberi">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in PASTI_LIBERI_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>
                        Media giornaliera di acqua assunta?
                        <span v-if="isFieldRequired('acquaMedia')" data-field="acquaMedia" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <select v-model="form.acquaMedia">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in ACQUA_MEDIA_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                  </div>
                  <label>
                    <span>
                      Scrivi qui eventuali note riguardanti l’alimentazione, più info ci dai e più potremo darti supporto…
                      <span v-if="isFieldRequired('noteAlimentazione')" data-field="noteAlimentazione" class="required-asterisk" aria-hidden="true">*</span>
                    </span>
                    <textarea v-model.trim="form.noteAlimentazione" rows="3"></textarea>
                  </label>
                  <label>
                    <span>
                      Quanto hai rispettato la dieta da 1 a 100?
                      <span v-if="isFieldRequired('adesioneAlimentazione')" data-field="adesioneAlimentazione" class="required-asterisk" aria-hidden="true">*</span>
                    </span>
                    <select v-model="form.adesioneAlimentazione">
                      <option value="">Seleziona…</option>
                      <option v-for="opt in ADESIONE_PERCENT_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                    </select>
                  </label>
                </div>
              </section>

              <!-- Recupero -->
              <section
                :class="['qi-block', { 'qi-block--active': isSectionActive('recupero') }]"
                v-show="isSectionActive('recupero')"
              >
                <header class="qi-block__header">
                  <h2 class="qi-block__title">Recupero</h2>
                </header>
                <div class="qi-block__body">
                  <div class="grid two">
                    <label>
                      <span>
                        Quante ore stai dormendo in media?
                        <span v-if="isFieldRequired('recuperoOreSonno')" data-field="recuperoOreSonno" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <select v-model="form.recuperoOreSonno">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in ORE_SONNO_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>
                        Il sonno è continuativo?
                        <span v-if="isFieldRequired('recuperoSonnoContinuativo')" data-field="recuperoSonnoContinuativo" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <select v-model="form.recuperoSonnoContinuativo">
                        <option value="">Seleziona…</option>
                        <option value="Si">Si</option>
                        <option value="No">No</option>
                      </select>
                    </label>
                  </div>
                  <label v-if="form.recuperoSonnoContinuativo === 'No'">
                    <span>
                      Se no, spiegaci il motivo
                      <span v-if="isFieldRequired('recuperoSonnoMotivo')" data-field="recuperoSonnoMotivo" class="required-asterisk" aria-hidden="true">*</span>
                    </span>
                    <textarea v-model.trim="form.recuperoSonnoMotivo" rows="2"></textarea>
                  </label>
                  <div class="grid two">
                    <label>
                      <span>
                        Come va il sonno rispetto al mese precedente?
                        <span v-if="isFieldRequired('recuperoSonnoTrend')" data-field="recuperoSonnoTrend" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <select v-model="form.recuperoSonnoTrend">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in TREND_SONNO_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>
                        Stai vivendo un periodo stressante?
                        <span v-if="isFieldRequired('recuperoStress')" data-field="recuperoStress" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <select v-model="form.recuperoStress">
                        <option value="">Seleziona…</option>
                        <option value="Si">Si</option>
                        <option value="No">No</option>
                      </select>
                    </label>
                  </div>
                </div>
              </section>

              <!-- Integrazione -->
              <section
                :class="['qi-block', { 'qi-block--active': isSectionActive('integrazione') }]"
                v-show="isSectionActive('integrazione')"
              >
                <header class="qi-block__header">
                  <h2 class="qi-block__title">Integrazione</h2>
                </header>
                <div class="qi-block__body">
                  <label>
                    <span>
                      Hai assunto integratori alimentari?
                      <span v-if="isFieldRequired('integrazioneAssunta')" data-field="integrazioneAssunta" class="required-asterisk" aria-hidden="true">*</span>
                    </span>
                    <select v-model="form.integrazioneAssunta">
                      <option value="">Seleziona…</option>
                      <option value="Si">Si</option>
                      <option value="No">No</option>
                    </select>
                  </label>
                  <label v-if="form.integrazioneAssunta === 'Si'">
                    <span>
                      Se sì, scrivili qui…
                      <span v-if="isFieldRequired('integrazioneDettagli')" data-field="integrazioneDettagli" class="required-asterisk" aria-hidden="true">*</span>
                    </span>
                    <textarea v-model.trim="form.integrazioneDettagli" rows="2"></textarea>
                  </label>
                </div>
              </section>

              <!-- Misurazioni -->
              <section
                :class="['qi-block', { 'qi-block--active': isSectionActive('misurazioni') }]"
                v-show="isSectionActive('misurazioni')"
              >
                <header class="qi-block__header">
                  <div class="qi-block__header-text">
                    <h2 class="qi-block__title">Misurazioni</h2>
                    <p class="qi-block__subtitle muted">
                      Circonferenza addome: 1 cm sopra l'ombelico • Coscia: metà tra anca e ginocchio • Fianchi: punto più largo dell'esterno coscia.
                    </p>
                  </div>
                </header>
                <div class="qi-block__body">
                  <div class="grid two">
                    <label>
                      <span>
                        Misure prese in preciclo/ciclo?
                        <span v-if="isFieldRequired('misurePreciclo')" data-field="misurePreciclo" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <select v-model="form.misurePreciclo">
                        <option value="">Seleziona…</option>
                        <option v-for="opt in YES_NO_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                    </label>
                    <label>
                      <span>
                        Peso (kg)
                        <span v-if="isFieldRequired('peso')" data-field="peso" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <input
                        v-model.trim="form.peso"
                        type="text"
                        inputmode="decimal"
                        pattern="[0-9.,+-]*"
                      />
                    </label>
                    <label>
                      <span>
                        Altezza (cm)
                        <span v-if="isFieldRequired('altezza')" data-field="altezza" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <input
                        v-model.trim="form.altezza"
                        type="text"
                        inputmode="decimal"
                        pattern="[0-9.,+-]*"
                      />
                    </label>
                    <label>
                      <span>
                        Fianchi (cm)
                        <span v-if="isFieldRequired('fianchi')" data-field="fianchi" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <input
                        v-model.trim="form.fianchi"
                        type="text"
                        inputmode="decimal"
                        pattern="[0-9.,+-]*"
                      />
                    </label>
                    <label>
                      <span>
                        Addome (cm)
                        <span v-if="isFieldRequired('addome')" data-field="addome" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <input
                        v-model.trim="form.addome"
                        type="text"
                        inputmode="decimal"
                        pattern="[0-9.,+-]*"
                      />
                    </label>
                    <label>
                      <span>
                        Torace (cm)
                        <span v-if="isFieldRequired('torace')" data-field="torace" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <input
                        v-model.trim="form.torace"
                        type="text"
                        inputmode="decimal"
                        pattern="[0-9.,+-]*"
                      />
                    </label>
                    <label>
                      <span>
                        Coscia (cm)
                        <span v-if="isFieldRequired('coscia')" data-field="coscia" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <input
                        v-model.trim="form.coscia"
                        type="text"
                        inputmode="decimal"
                        pattern="[0-9.,+-]*"
                      />
                    </label>
                    <label>
                      <span>
                        Braccio (cm)
                        <span v-if="isFieldRequired('braccio')" data-field="braccio" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <input
                        v-model.trim="form.braccio"
                        type="text"
                        inputmode="decimal"
                        pattern="[0-9.,+-]*"
                      />
                    </label>
                    <label>
                      <span>
                        Massa magra (%)
                        <span v-if="isFieldRequired('massaMagra')" data-field="massaMagra" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <input
                        v-model.trim="form.massaMagra"
                        type="text"
                        inputmode="decimal"
                        pattern="[0-9.,+-]*"
                      />
                    </label>
                    <label>
                      <span>
                        Massa grassa (%)
                        <span v-if="isFieldRequired('massaGrassa')" data-field="massaGrassa" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <input
                        v-model.trim="form.massaGrassa"
                        type="text"
                        inputmode="decimal"
                        pattern="[0-9.,+-]*"
                      />
                    </label>
                    <label>
                      <span>
                        Acqua corporea (%)
                        <span v-if="isFieldRequired('acquaCorporea')" data-field="acquaCorporea" class="required-asterisk" aria-hidden="true">*</span>
                      </span>
                      <input
                        v-model.trim="form.acquaCorporea"
                        type="text"
                        inputmode="decimal"
                        pattern="[0-9.,+-]*"
                      />
                    </label>
                  </div>
                  <label>
                    <span>
                      Note sulle misurazioni
                      <span v-if="isFieldRequired('misurazioniNote')" data-field="misurazioniNote" class="required-asterisk" aria-hidden="true">*</span>
                    </span>
                    <textarea v-model.trim="form.misurazioniNote" rows="3"></textarea>
                  </label>
                </div>
              </section>

              <!-- Foto -->
              <section
                :class="['qi-block', { 'qi-block--active': isSectionActive('foto') }]"
                v-show="isSectionActive('foto')"
              >
                <header class="qi-block__header">
                  <div class="qi-block__header-text">
                    <h2 class="qi-block__title">Foto</h2>
                    <p class="photo-intro">Carica foto a figura intera, con buona luce e qualità.</p>
                  </div>
                </header>
                <div class="qi-block__body">
                  <div class="photo-grid">
                    <div class="photo-card">
                      <h3 class="photo-card__title">
                        Foto frontale
                        <span v-if="isFieldRequired('foto.anteriore')" data-field="foto.anteriore" class="required-asterisk" aria-hidden="true">*</span>
                      </h3>
                      <p class="photo-card__description">Braccia lungo i fianchi, gambe leggermente divaricate.</p>
                      <div class="photo-card__upload">
                        <span class="photo-card__upload-label">Carica file</span>
                        <input type="file" accept="image/*" @change="onPhotoChange('anteriore', $event)" />
                      </div>
                      <div class="photo-card__status">
                        <div v-if="uploadingPhoto.anteriore" class="upload-progress">
                          <span class="spinner"></span>
                          <span>Caricamento…</span>
                        </div>
                        <div v-else-if="form.foto.anteriore" class="file-meta">
                          <div v-if="isImageFile(form.foto.anteriore)" class="file-preview">
                            <img
                              :src="form.foto.anteriore.url"
                              alt="Anteprima foto frontale"
                              loading="lazy"
                            />
                          </div>
                          <a :href="form.foto.anteriore.url" target="_blank" rel="noopener">{{ form.foto.anteriore.name }}</a>
                          <button type="button" @click="removePhoto('anteriore')">Elimina</button>
                        </div>
                      </div>
                    </div>
                    <div class="photo-card">
                      <h3 class="photo-card__title">
                        Foto posteriore
                        <span v-if="isFieldRequired('foto.posteriore')" data-field="foto.posteriore" class="required-asterisk" aria-hidden="true">*</span>
                      </h3>
                      <p class="photo-card__description">Braccia lungo i fianchi, capelli legati o in avanti.</p>
                      <div class="photo-card__upload">
                        <span class="photo-card__upload-label">Carica file</span>
                        <input type="file" accept="image/*" @change="onPhotoChange('posteriore', $event)" />
                      </div>
                      <div class="photo-card__status">
                        <div v-if="uploadingPhoto.posteriore" class="upload-progress">
                          <span class="spinner"></span>
                          <span>Caricamento…</span>
                        </div>
                        <div v-else-if="form.foto.posteriore" class="file-meta">
                          <div v-if="isImageFile(form.foto.posteriore)" class="file-preview">
                            <img
                              :src="form.foto.posteriore.url"
                              alt="Anteprima foto posteriore"
                              loading="lazy"
                            />
                          </div>
                          <a :href="form.foto.posteriore.url" target="_blank" rel="noopener">{{ form.foto.posteriore.name }}</a>
                          <button type="button" @click="removePhoto('posteriore')">Elimina</button>
                        </div>
                      </div>
                    </div>
                    <div class="photo-card">
                      <h3 class="photo-card__title">
                        Foto profilo sinistro
                        <span v-if="isFieldRequired('foto.profiloSinistra')" data-field="foto.profiloSinistra" class="required-asterisk" aria-hidden="true">*</span>
                      </h3>
                      <p class="photo-card__description">Braccia avanti e tese, capelli raccolti.</p>
                      <div class="photo-card__upload">
                        <span class="photo-card__upload-label">Carica file</span>
                        <input type="file" accept="image/*" @change="onPhotoChange('profiloSinistra', $event)" />
                      </div>
                      <div class="photo-card__status">
                        <div v-if="uploadingPhoto.profiloSinistra" class="upload-progress">
                          <span class="spinner"></span>
                          <span>Caricamento…</span>
                        </div>
                        <div v-else-if="form.foto.profiloSinistra" class="file-meta">
                          <div v-if="isImageFile(form.foto.profiloSinistra)" class="file-preview">
                            <img
                              :src="form.foto.profiloSinistra.url"
                              alt="Anteprima foto profilo sinistro"
                              loading="lazy"
                            />
                          </div>
                          <a :href="form.foto.profiloSinistra.url" target="_blank" rel="noopener">{{ form.foto.profiloSinistra.name }}</a>
                          <button type="button" @click="removePhoto('profiloSinistra')">Elimina</button>
                        </div>
                      </div>
                    </div>
                    <div class="photo-card">
                      <h3 class="photo-card__title">
                        Foto profilo destro
                        <span v-if="isFieldRequired('foto.profiloDestra')" data-field="foto.profiloDestra" class="required-asterisk" aria-hidden="true">*</span>
                      </h3>
                      <p class="photo-card__description">Braccia avanti e tese, capelli raccolti.</p>
                      <div class="photo-card__upload">
                        <span class="photo-card__upload-label">Carica file</span>
                        <input type="file" accept="image/*" @change="onPhotoChange('profiloDestra', $event)" />
                      </div>
                      <div class="photo-card__status">
                        <div v-if="uploadingPhoto.profiloDestra" class="upload-progress">
                          <span class="spinner"></span>
                          <span>Caricamento…</span>
                        </div>
                        <div v-else-if="form.foto.profiloDestra" class="file-meta">
                          <div v-if="isImageFile(form.foto.profiloDestra)" class="file-preview">
                            <img
                              :src="form.foto.profiloDestra.url"
                              alt="Anteprima foto profilo destro"
                              loading="lazy"
                            />
                          </div>
                          <a :href="form.foto.profiloDestra.url" target="_blank" rel="noopener">{{ form.foto.profiloDestra.name }}</a>
                          <button type="button" @click="removePhoto('profiloDestra')">Elimina</button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <!-- Note generali -->
              <section
                :class="['qi-block', { 'qi-block--active': isSectionActive('note') }]"
                v-show="isSectionActive('note')"
              >
                <header class="qi-block__header">
                  <h2 class="qi-block__title">Note generali</h2>
                </header>
                <div class="qi-block__body">
                  <label>
                    <span>
                      Scrivi qui ogni comunicazione necessaria a valutare questo programma, includendo le informazioni più importanti condivise con lo staff.
                      <span v-if="isFieldRequired('noteGenerali')" data-field="noteGenerali" class="required-asterisk" aria-hidden="true">*</span>
                    </span>
                    <textarea v-model.trim="form.noteGenerali" rows="4"></textarea>
                  </label>
                </div>
              </section>

              <nav v-if="!isLastSection" class="puq-section-nav" :class="{ 'puq-section-nav--first': isFirstSection }" aria-label="Sezioni del check finale">
                <button
                  v-if="!isFirstSection"
                  type="button"
                  class="nav-button ghost"
                  @click="goToPreviousSection"
                >
                  Indietro
                </button>
                <div class="puq-nav-copy"><span>Sezione {{ activeSectionIndex + 1 }} di {{ sectionFlow.length }}</span><small>Prossima: {{ nextSectionTitle }}</small></div>
                <button type="button" class="nav-button primary" @click="goToNextSection" :disabled="saving || completingStep">
                  Avanti <span aria-hidden="true">→</span>
                </button>
              </nav>

              <footer class="form-footer" v-show="isLastSection">
                <div class="left">
                  <button v-if="!isFirstSection" type="button" class="secondary-button ghost-back" @click="goToPreviousSection">Indietro</button>
                  <button type="submit" class="primary-button" :disabled="saving || loading">
                    {{ saving ? 'Salvataggio…' : isManualStandalone ? 'Salva check' : 'Salva bozza' }}
                  </button>
                  <button
                    v-if="showCompleteButton"
                    type="button"
                    class="primary-button complete-button"
                    :disabled="saving || loading || completingStep || !canCompleteStep"
                    @click="markStepCompleted"
                  >
                    {{ completingStep ? 'Completamento…' : 'Invia Check' }}
                  </button>
                  <button
                    type="button"
                    class="secondary-button"
                    :disabled="saving || loading || !dirty"
                    @click="resetForm"
                  >
                    Annulla modifiche
                  </button>
                </div>
                <div class="right">
                  <span v-if="dirty" class="dirty-indicator">Modifiche non salvate</span>
                </div>
                <p v-if="showCompleteButton && !canCompleteStep" class="muted" role="status">Puoi salvare la bozza. Per inviarla, riapri il check dalla pagina Percorsi.</p>
              </footer>
        </form>

        <transition name="fade">
          <div v-if="successMessage" class="puq-banner puq-banner--success">{{ successMessage }}</div>
        </transition>
      </template>
    </div>
  </div>
</template>

<script>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router';
import UserAppChrome from './UserAppChrome.vue';
import { findUserFormField, focusUserFormSection } from '@/lib/userFormNavigation';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import {
  deleteObject,
  getDownloadURL,
  getStorage,
  ref as storageRef,
  uploadBytesResumable,
} from 'firebase/storage';

import {
  flattenAnswers,
  groupEntries,
  prettifyKey,
  sanitizeQuestionarioAnswers,
} from '../../lib/questionarioDisplay.js';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { buildQuestionarioPdfFileName } from '@/lib/pdfExport';
import { generateCheckFinalePdf } from '@/lib/programmiPdf';
import { setStepDone } from '@/lib/percorsi';
import { computeProgrammeLocks } from '@/lib/programmeLocks';

const QUESTIONARI_COLLECTION = 'questionari';
const QUESTIONARIO_TYPE = 'user_programmi_check_finale';
const QUESTIONARIO_TEMPLATE = {
  id: 'user_programmi_check_finale_v1',
  key: 'user_programmi_check_finale',
  label: 'Check Finale Programmi',
  version: 1,
};

const SEDUTE_SETTIMANALI_OPTIONS = ['1', '2', '3', '4', '5', '5+'];
const ADESIONE_PERCENT_OPTIONS = ['10', '20', '30', '40', '50', '60', '70', '80', '90', '100'];
const STRATEGIA_ALIMENTARE_OPTIONS = ['Si', 'No', 'A volte'];
const RISPETTO_GRAMMATURE_OPTIONS = ['Si', 'No, ne ho mangiate meno', 'No, ne ho mangiate di più'];
const PASTI_LIBERI_OPTIONS = ['4', 'Meno di 4', 'Più di 4'];
const ACQUA_MEDIA_OPTIONS = ['1', '2', '3', '4'];
const ORE_SONNO_OPTIONS = ['Meno di 5', '6', '7', '8', '9', 'Più di 9'];
const TREND_SONNO_OPTIONS = ['Meglio', 'Peggio', 'Stabile'];
const YES_NO_OPTIONS = ['Si', 'No', 'sono uomo'];

const QUESTIONARIO_INITIAL_TYPE = 'user_programmi_questionario';

const FORM_SECTION_FLOW = Object.freeze([
  { id: 'allenamento', title: 'Allenamento' },
  { id: 'alimentazione', title: 'Alimentazione' },
  { id: 'recupero', title: 'Recupero' },
  { id: 'integrazione', title: 'Integrazione' },
  { id: 'misurazioni', title: 'Misurazioni' },
  { id: 'foto', title: 'Foto' },
  { id: 'note', title: 'Note generali' },
]);

const deepClone = (value) => JSON.parse(JSON.stringify(value ?? null));
const INVALID_STORAGE_CHARS = /[\\/?%*:|"<>#[\]]/g;
const sanitizeName = (name = '') => {
  const cleaned = name.replace(INVALID_STORAGE_CHARS, '').replace(/\s+/g, '_');
  let result = '';
  for (const ch of cleaned) {
    if (ch.charCodeAt(0) >= 32) result += ch;
  }
  return result;
};

const toDateSafe = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') {
    const d = value.toDate();
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
};

const formatDate = (value) => {
  const date = toDateSafe(value);
  if (!date) return '';
  return new Intl.DateTimeFormat('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const toSnapshotIso = (value) => {
  const date = toDateSafe(value);
  return date ? date.toISOString() : null;
};

const defaultForm = () => ({
  seduteSettimanali: '',
  attivitaAffiancate: '',
  noteAllenamento: '',
  adesioneAllenamento: '',
  strategiaAlimentare: '',
  rispettoGrammature: '',
  pastiLiberi: '',
  acquaMedia: '',
  noteAlimentazione: '',
  adesioneAlimentazione: '',
  recuperoOreSonno: '',
  recuperoSonnoContinuativo: '',
  recuperoSonnoMotivo: '',
  recuperoSonnoTrend: '',
  recuperoStress: '',
  integrazioneAssunta: '',
  integrazioneDettagli: '',
  misurePreciclo: '',
  peso: null,
  altezza: null,
  fianchi: null,
  addome: null,
  torace: null,
  coscia: null,
  braccio: null,
  massaMagra: null,
  massaGrassa: null,
  acquaCorporea: null,
  misurazioniNote: '',
  foto: {
    posteriore: null,
    anteriore: null,
    profiloDestra: null,
    profiloSinistra: null,
  },
  noteGenerali: '',
});

const toPathSegments = (path) =>
  String(path)
    .split('.')
    .map((segment) => segment.trim())
    .filter(Boolean);

const readValueByPath = (state, path) =>
  toPathSegments(path).reduce((acc, segment) => (acc == null ? acc : acc[segment]), state);

const parseLooseNumber = (value) => {
  if (value === null || value === undefined) return NaN;
  if (typeof value === 'number') return Number.isFinite(value) ? value : NaN;
  const str = String(value).trim();
  if (!str) return NaN;
  const normalized = str.replace(/,/g, '.');
  const num = parseFloat(normalized);
  return Number.isFinite(num) ? num : NaN;
};

const isPositiveNumber = (value) => Number.isFinite(parseLooseNumber(value));

const hasUploadedFile = (metadata) => !!(metadata && (metadata.url || metadata.path));

const isValueEmpty = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
};

const PARTIAL_OPTIONAL_PATHS = new Set([
  'misurePreciclo',
  'peso',
  'altezza',
  'fianchi',
  'addome',
  'torace',
  'coscia',
  'braccio',
  'massaMagra',
  'massaGrassa',
  'acquaCorporea',
  'foto.anteriore',
  'foto.posteriore',
  'foto.profiloSinistra',
  'foto.profiloDestra',
]);

const REQUIRED_FIELDS = Object.freeze([
  { path: 'seduteSettimanali', label: 'Sedute settimanali' },
  { path: 'attivitaAffiancate', label: 'Attività sportive affiancate', optional: true },
  { path: 'noteAllenamento', label: "Note sull'allenamento", optional: true },
  { path: 'adesioneAllenamento', label: 'Adesione allenamento' },
  { path: 'strategiaAlimentare', label: 'Strategia alimentare' },
  { path: 'rispettoGrammature', label: 'Rispetto grammature' },
  { path: 'pastiLiberi', label: 'Pasti liberi' },
  { path: 'acquaMedia', label: "Acqua assunta" },
  { path: 'noteAlimentazione', label: "Note sull'alimentazione", optional: true },
  { path: 'adesioneAlimentazione', label: 'Adesione alimentazione' },
  { path: 'recuperoOreSonno', label: 'Ore di sonno' },
  { path: 'recuperoSonnoContinuativo', label: 'Sonno continuativo' },
  {
    path: 'recuperoSonnoMotivo',
    label: 'Motivo sonno non continuativo',
    when: (state) => state.recuperoSonnoContinuativo === 'No',
    optional: true,
  },
  { path: 'recuperoSonnoTrend', label: 'Trend sonno' },
  { path: 'recuperoStress', label: 'Periodo stressante' },
  { path: 'integrazioneAssunta', label: 'Integrazione assunta' },
  {
    path: 'integrazioneDettagli',
    label: "Dettagli sull'integrazione",
    when: (state) => state.integrazioneAssunta === 'Si',
    optional: true,
  },
  { path: 'peso', label: 'Peso', validator: (value) => isPositiveNumber(value) },
  { path: 'altezza', label: 'Altezza', validator: (value) => isPositiveNumber(value) },
  { path: 'fianchi', label: 'Fianchi', validator: (value) => isPositiveNumber(value) },
  { path: 'addome', label: 'Addome', validator: (value) => isPositiveNumber(value) },
  { path: 'coscia', label: 'Coscia', validator: (value) => isPositiveNumber(value) },
  { path: 'misurazioniNote', label: 'Note sulle misurazioni', optional: true },
  { path: 'foto.anteriore', label: 'Foto frontale', validator: (value) => hasUploadedFile(value) },
  { path: 'foto.posteriore', label: 'Foto posteriore', validator: (value) => hasUploadedFile(value) },
  { path: 'foto.profiloSinistra', label: 'Foto profilo sinistro', validator: (value) => hasUploadedFile(value) },
  { path: 'foto.profiloDestra', label: 'Foto profilo destro', validator: (value) => hasUploadedFile(value) },
  { path: 'noteGenerali', label: 'Note generali', optional: true },
]);

const pickQueryValue = (key, route) => {
  const raw = route.query?.[key];
  if (Array.isArray(raw)) return raw[0] ?? null;
  return raw ?? null;
};

const toTrimmedStringOrNull = (value) => {
  if (value === undefined || value === null) return null;
  const str = String(value);
  const trimmed = str.trim();
  return trimmed ? trimmed : null;
};

const isTruthyManualStandalone = (value) => {
  if (value === true) return true;
  if (value === 1) return true;
  if (value === '1') return true;
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    return v === 'true' || v === 'yes';
  }
  return false;
};

const joinNameParts = (...parts) => {
  const filtered = parts
    .map((part) => toTrimmedStringOrNull(part))
    .filter(Boolean);
  return filtered.join(' ').trim();
};

const toStepIndex = (value) => {
  if (typeof value !== 'number' && typeof value !== 'string') return null;
  if (typeof value === 'string' && !value.trim()) return null;
  const num = Number(value);
  return Number.isSafeInteger(num) && num >= 0 ? num : null;
};

export default {
  name: 'ProgrammiUserCheckFinale',
  components: { UserAppChrome },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const db = getFirestore();
    const storage = getStorage();
    const auth = getAuth();

    const form = reactive(defaultForm());
    const lastLoaded = ref(deepClone(form));
    const questionarioDocId = ref('');
    const loadedDocUid = ref('');
    const docPercorsoId = ref('');
    const docStepIndex = ref(null);
    const manualStandalone = ref(false);
    const preferredDocId = computed(() => {
      const candidates = [
        pickQueryValue('questionarioDocId', route),
        pickQueryValue('questionarioUid', route),
        pickQueryValue('questionnaireDocId', route),
        pickQueryValue('questionnaireId', route),
        pickQueryValue('docId', route),
      ];
      for (const raw of candidates) {
        const value = toTrimmedStringOrNull(raw);
        if (value) return value;
      }
      return null;
    });
    const isManualStandalone = computed(() =>
      isTruthyManualStandalone(pickQueryValue('manualStandalone', route)) || manualStandalone.value
    );
    const showCompleteButton = computed(() => !!questionarioDocId.value && !isManualStandalone.value);
    const questionarioInfo = ref({ createdAt: null, updatedAt: null, templateLabel: QUESTIONARIO_TEMPLATE.label });
    const allowPartialSubmission = ref(false);
    const setDocContext = (data = {}) => {
      docPercorsoId.value = toTrimmedStringOrNull(data?.percorsoId) || '';
      docStepIndex.value = toStepIndex(data?.stepIndex);
    };
    const clearDocContext = () => {
      docPercorsoId.value = '';
      docStepIndex.value = null;
    };
    const pdfExporting = ref(false);
    const missingMandatoryFields = ref([]);
    const completingStep = ref(false);
    const canCompleteStep = computed(() => {
      const ctx = percorsoContext.value;
      return !!(ctx.percorsoId && Number.isInteger(ctx.stepIndex));
    });
    const suppressDirty = ref(false);

    const loading = ref(true);
    const saving = ref(false);
    const dirty = ref(false);
    const errorMessage = ref('');
    const lockWarning = ref('');
    const successMessage = ref('');
    const submittedLock = ref(false);
    const stepDoneFlag = ref(false);
    const adminNeedsUpdate = ref(false);
    const adminApproved = ref(false);
    const approvalAlerted = ref(false);
    const submittedLockMessage = 'Check già inviato: contatta il supporto per eventuali modifiche.';
    const approvalLockMessage = 'Check già approvato';
    const isHardLocked = computed(() => submittedLock.value && !adminNeedsUpdate.value);
    const isAdminApproved = computed(() => adminApproved.value);
    let successTimer = null;
    const actionMessage = ref('');
    const actionMessageType = ref('success');
    let actionTimer = null;

    const currentUserId = ref(auth.currentUser?.uid || '');
    const authResolved = ref(false);
    const linkUserId = computed(() => toTrimmedStringOrNull(pickQueryValue('userId', route)));
    // preferisci sempre l'uid passato nel link per risolvere l'utente target
    const effectiveUserId = computed(() => linkUserId.value || loadedDocUid.value || currentUserId.value || '');
    const explicitLinkUserName = computed(() => {
      const combined = joinNameParts(pickQueryValue('userName', route), pickQueryValue('userSurname', route));
      if (combined) return combined;
      return toTrimmedStringOrNull(pickQueryValue('userFullName', route));
    });
    const fetchedLinkUserName = ref('');
    let lastIdentityRequest = 0;
    watch(
      [linkUserId, explicitLinkUserName],
      async ([targetUid, explicitName]) => {
        lastIdentityRequest += 1;
        const requestId = lastIdentityRequest;
        if (!targetUid || explicitName) {
          fetchedLinkUserName.value = '';
          return;
        }
        try {
          const snap = await getDoc(doc(db, 'users', targetUid));
          if (requestId !== lastIdentityRequest) return;
          if (snap.exists()) {
            const data = snap.data() || {};
            fetchedLinkUserName.value =
              joinNameParts(data.name || data.firstName, data.surname || data.lastName) ||
              toTrimmedStringOrNull(data.displayName) ||
              '';
          } else {
            fetchedLinkUserName.value = '';
          }
        } catch (err) {
          if (requestId === lastIdentityRequest) {
            fetchedLinkUserName.value = '';
          }
          console.warn('Impossibile recuperare il nome collegato al check finale:', err);
        }
      },
      { immediate: true }
    );
    const linkUserDisplayName = computed(
      () => explicitLinkUserName.value || fetchedLinkUserName.value || 'questo utente'
    );
    const loginRoute = computed(() => ({
      path: '/',
      query: route.fullPath ? { next: route.fullPath } : undefined,
    }));
    const linkMismatchWarning = computed(() => {
      const targetUid = linkUserId.value;
      if (!targetUid) return '';
      if (!authResolved.value) return '';
      const currentUid = currentUserId.value || '';
      const resolvedUid = effectiveUserId.value || '';
      if (resolvedUid === targetUid || currentUid === targetUid) return '';
      if (!currentUid) {
        return `Check di "${linkUserDisplayName.value}": per compilarlo effettua il login, altrimenti contatta l'assistenza per ricevere il tuo link.`;
      }
      return `Check di "${linkUserDisplayName.value}": per compilarlo effettua il login, altrimenti contatta l'assistenza per ricevere il tuo link.`;
    });

    const evaluatePercorsoLock = async () => {
      const ctx = percorsoContext.value;
      const uid = effectiveUserId.value;
      const percorsoId = ctx.percorsoId || docPercorsoId.value;
      const stepIndex = Number.isInteger(ctx.stepIndex) ? ctx.stepIndex : docStepIndex.value;
      if (!uid || !percorsoId) {
        lockWarning.value = '';
        submittedLock.value = false;
        adminNeedsUpdate.value = false;
        stepDoneFlag.value = false;
        adminApproved.value = false;
        approvalAlerted.value = false;
        return;
      }
      try {
        const snap = await getDocs(query(collection(db, 'percorsi'), where('uid', '==', uid)));
        const percorsi = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        const target = percorsi.find(p => p.id === percorsoId);
        if (!target) {
          lockWarning.value = '';
          submittedLock.value = false;
          adminNeedsUpdate.value = false;
          stepDoneFlag.value = false;
          adminApproved.value = false;
          approvalAlerted.value = false;
          return;
        }
        if (target.historyLocked) {
          lockWarning.value = 'Prima di compilare questo hai dei questionari o check da completare';
          adminApproved.value = false;
          approvalAlerted.value = false;
          return;
        }
        const { sequentialLocks, previousLocks } = computeProgrammeLocks(percorsi);
        const isBlocked = previousLocks[target.id] || sequentialLocks[target.id];
        lockWarning.value = isBlocked ? 'Prima di compilare questo hai dei questionari o check da completare' : '';
        const steps = Array.isArray(target.steps) ? target.steps : [];
        const step = Number.isInteger(stepIndex) ? steps[stepIndex] : null;
        stepDoneFlag.value = !!(step && step.done);
        adminNeedsUpdate.value = (step?.adminStatus || '') === 'needs_update';
        adminApproved.value = (step?.adminStatus || '') === 'approved';
        submittedLock.value = stepDoneFlag.value && !adminNeedsUpdate.value;
        if (adminApproved.value) {
          showApprovedLockMessage();
        } else {
          approvalAlerted.value = false;
        }
      } catch (err) {
        console.warn('Impossibile verificare il blocco del percorso', err);
        lockWarning.value = '';
        submittedLock.value = false;
        adminNeedsUpdate.value = false;
        stepDoneFlag.value = false;
        adminApproved.value = false;
        approvalAlerted.value = false;
      }
    };

    const uploadingPhoto = reactive({});
    const requiredFieldMap = REQUIRED_FIELDS.reduce((acc, field) => {
      acc[field.path] = field;
      return acc;
    }, {});

    const isFieldRequired = (path) => {
      const field = requiredFieldMap[path];
      if (!field) return false;
      if (field.optional) return false;
      if (allowPartialSubmission.value && PARTIAL_OPTIONAL_PATHS.has(path)) return false;
      if (typeof field.when === 'function' && !field.when(form)) return false;
      return true;
    };
    const sectionFlow = FORM_SECTION_FLOW;
    const sectionIndexMap = sectionFlow.reduce((acc, section, index) => {
      acc[section.id] = index;
      return acc;
    }, {});
    const activeSectionIndex = ref(0);
    const formRootRef = ref(null);
    const stepHeadingRef = ref(null);
    const actionMessageRef = ref(null);
    const requiredSummaryRef = ref(null);
    const isFirstSection = computed(() => activeSectionIndex.value === 0);
    const isLastSection = computed(() => activeSectionIndex.value === sectionFlow.length - 1);
    const currentSection = computed(() => sectionFlow[activeSectionIndex.value] || sectionFlow[0]);
    const nextSectionTitle = computed(() =>
      isLastSection.value ? '' : sectionFlow[activeSectionIndex.value + 1]?.title || ''
    );
    const progressValue = computed(() => {
      if (!sectionFlow.length) return 100;
      return Math.round(((activeSectionIndex.value + 1) / sectionFlow.length) * 100);
    });

    const scrollToActiveSection = () => {
      if (typeof window === 'undefined') return;
      nextTick(() => {
        focusUserFormSection(stepHeadingRef.value?.closest?.('.puq-stepper') || stepHeadingRef.value || formRootRef.value, { focusTarget: stepHeadingRef.value });
      });
    };

    const validateMandatoryFields = () => {
      const errors = [];
      REQUIRED_FIELDS.forEach((field) => {
        if (field.optional) return;
        if (allowPartialSubmission.value && PARTIAL_OPTIONAL_PATHS.has(field.path)) {
          return;
        }
        if (typeof field.when === 'function' && !field.when(form)) {
          return;
        }
        const value = readValueByPath(form, field.path);
        const valid = field.validator ? field.validator(value, form) : !isValueEmpty(value);
        if (!valid) {
          errors.push({ id: field.path, label: field.label });
        }
      });
      return errors;
    };

    const alertMissingFields = (errors) => {
      if (errors.length) nextTick(() => focusUserFormSection(requiredSummaryRef.value));
    };

    const goToSectionIndex = (index) => {
      if (index < 0 || index >= sectionFlow.length) return;
      if (index === activeSectionIndex.value) return;
      activeSectionIndex.value = index;
    };

    const goToSection = (idOrIndex) => {
      if (typeof idOrIndex === 'number') {
        goToSectionIndex(idOrIndex);
        return;
      }
      const idx = sectionIndexMap[idOrIndex];
      if (idx == null) return;
      goToSectionIndex(idx);
    };

    const goToNextSection = () => {
      if (isLastSection.value) return;
      goToSectionIndex(activeSectionIndex.value + 1);
    };

    const goToPreviousSection = () => {
      if (isFirstSection.value) return;
      goToSectionIndex(activeSectionIndex.value - 1);
    };
    const handleFormSubmit = () => isLastSection.value ? saveQuestionario() : goToNextSection();
    const goToMissingField = async (fieldId) => {
      const destination = findUserFormField(formRootRef.value, fieldId);
      if (!destination) return;
      goToSectionIndex(destination.sectionIndex);
      await nextTick();
      await nextTick();
      focusUserFormSection(destination.target, { focusTarget: destination.control });
    };

    const isSectionActive = (id) => sectionIndexMap[id] === activeSectionIndex.value;

    const setUploadState = (store, key, value) => {
      store[key] = value;
    };

    const isImageFile = (metadata) => {
      if (!metadata) return false;
      const type = String(metadata.contentType || '').toLowerCase();
      if (type.startsWith('image/')) return true;
      const name = String(metadata.name || '').toLowerCase();
      return /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(name);
    };

    const percorsoContext = computed(() => {
      const stepIndex = toStepIndex(pickQueryValue('stepIndex', route));
      const sequenceIndex = toStepIndex(pickQueryValue('sequenceIndex', route));
      const sequenceTotal = toStepIndex(pickQueryValue('sequenceTotal', route));
      return {
        percorsoId: toTrimmedStringOrNull(pickQueryValue('percorsoId', route)),
        stepIndex,
        stepKey: toTrimmedStringOrNull(pickQueryValue('stepKey', route)) || 'check_finale',
        sequenceGroupId: toTrimmedStringOrNull(pickQueryValue('sequenceGroupId', route)),
        sequenceIndex,
        sequenceTotal,
        productId: toTrimmedStringOrNull(pickQueryValue('productId', route)),
        productName: toTrimmedStringOrNull(pickQueryValue('productName', route)),
        programTitle: toTrimmedStringOrNull(pickQueryValue('programTitle', route)),
        programLabel: toTrimmedStringOrNull(pickQueryValue('programLabel', route)),
        plannedStartAt: toTrimmedStringOrNull(pickQueryValue('plannedStartAt', route)),
        expireAt: toTrimmedStringOrNull(pickQueryValue('expireAt', route)),
        stepName: toTrimmedStringOrNull(pickQueryValue('stepName', route)),
      };
    });

    const questionarioTargetKey = computed(() => {
      const ctx = percorsoContext.value;
      return [
        effectiveUserId.value || '',
        ctx.percorsoId || '',
        Number.isInteger(ctx.stepIndex) ? String(ctx.stepIndex) : 'x',
        ctx.stepKey || '',
      ].join('__');
    });

    let detachAuthListener = null;

    const showActionMessage = (message, type = 'success') => {
      if (actionTimer) clearTimeout(actionTimer);
      actionMessageType.value = type;
      actionMessage.value = message;
      if (message && type !== 'error') {
        actionTimer = setTimeout(() => {
          actionMessage.value = '';
        }, 4000);
      }
      if (message && type === 'error') nextTick(() => focusUserFormSection(actionMessageRef.value));
    };
    const showApprovedLockMessage = () => {
      showActionMessage(approvalLockMessage, 'error');
      if (typeof window !== 'undefined' && !approvalAlerted.value) {
        window.alert(approvalLockMessage);
        approvalAlerted.value = true;
      }
    };

    onBeforeRouteLeave(() => !dirty.value || typeof window === 'undefined' || window.confirm('Hai modifiche non salvate. Uscire comunque dal check?'));
    const goBack = () => {
      if (typeof window !== 'undefined' && window.history.length > 1) {
        router.back();
      } else {
        router.push('/user-programmi');
      }
    };

    const plainAnswers = () => deepClone(form);

    const exportFormAsPdf = async () => {
      if (pdfExporting.value) return;
      pdfExporting.value = true;
      showActionMessage('Sto generando il PDF…');
      try {
        const exportUserId = effectiveUserId.value || questionarioDocId.value || 'utente';
        const fileName = buildQuestionarioPdfFileName({
          prefix: 'check_finale',
          userId: exportUserId,
          updatedAt: questionarioInfo.value.updatedAt,
        });
        await generateCheckFinalePdf({
          answers: plainAnswers(),
          meta: {
            userId: exportUserId,
            userLabel: linkUserDisplayName.value,
            questionarioInfo: questionarioInfo.value,
            percorso: percorsoContext.value,
          },
          fileName,
        });
        showActionMessage('PDF salvato nei download.');
      } catch (err) {
        console.error('exportFormAsPdf error:', err);
        showActionMessage('Impossibile generare il PDF.', 'error');
      } finally {
        pdfExporting.value = false;
      }
    };
    const initialQuestionarioAnswers = ref({});
    const initialQuestionarioInfo = ref({ createdAt: null, updatedAt: null, templateLabel: null });
    const initialLoading = ref(false);
    const initialError = ref('');
    const initialSearch = ref('');
    const checkSearch = ref('');

    const currentAnswers = computed(() => plainAnswers());
    const sanitizedInitialAnswers = computed(() =>
      sanitizeQuestionarioAnswers(initialQuestionarioAnswers.value || {})
    );
    const sanitizedCheckAnswers = computed(() => sanitizeQuestionarioAnswers(currentAnswers.value || {}));
    const buildInitialSnapshotPayload = () => {
      const answers = sanitizedInitialAnswers.value || {};
      if (!Object.keys(answers).length) return {};
      const info = initialQuestionarioInfo.value || {};
      return {
        initialAnswersSnapshot: answers,
        initialQuestionarioInfoSnapshot: {
          createdAt: toSnapshotIso(info.createdAt),
          updatedAt: toSnapshotIso(info.updatedAt),
          templateLabel: info.templateLabel || null,
        },
      };
    };

    const groupedInitialEntries = computed(() =>
      groupEntries(flattenAnswers(sanitizedInitialAnswers.value), initialSearch.value, {
        answers: sanitizedInitialAnswers.value,
      })
    );

    const groupedCheckEntries = computed(() =>
      groupEntries(flattenAnswers(sanitizedCheckAnswers.value), checkSearch.value, {
        answers: sanitizedCheckAnswers.value,
      })
    );

    const setFormData = (data = {}, docData = null) => {
      const base = defaultForm();
      const source = { ...(data || {}) };

      Object.keys(base).forEach((key) => {
        if (key === 'foto') {
          const incoming = source.foto || {};
          const legacyProfile = incoming.profilo || null;
          base.foto.posteriore = incoming.posteriore || null;
          base.foto.anteriore = incoming.anteriore || null;
          base.foto.profiloDestra = incoming.profiloDestra || legacyProfile || null;
          base.foto.profiloSinistra = incoming.profiloSinistra || legacyProfile || null;
        } else {
          base[key] = source[key] !== undefined ? source[key] : base[key];
        }
      });

      suppressDirty.value = true;
      Object.assign(form, base);
      lastLoaded.value = deepClone(form);
      suppressDirty.value = false;

      if (docData) {
        questionarioInfo.value = {
          createdAt: toDateSafe(docData.createdAt) || null,
          updatedAt: toDateSafe(docData.updatedAt || docData.createdAt) || null,
          templateLabel: docData.templateLabel || QUESTIONARIO_TEMPLATE.label,
        };
      } else {
        questionarioInfo.value = { createdAt: null, updatedAt: null, templateLabel: QUESTIONARIO_TEMPLATE.label };
      }

      dirty.value = false;
      errorMessage.value = '';
      successMessage.value = '';
      missingMandatoryFields.value = [];
    };

    const loadInitialQuestionario = async () => {
      initialLoading.value = true;
      initialError.value = '';
      initialQuestionarioAnswers.value = {};
      initialQuestionarioInfo.value = { createdAt: null, updatedAt: null, templateLabel: null };

      const uid = effectiveUserId.value;
      if (!uid) {
        initialLoading.value = false;
        return;
      }

      try {
        const ctx = percorsoContext.value;
        const constraints = [
          where('uid', '==', uid),
          where('type', '==', QUESTIONARIO_INITIAL_TYPE),
        ];
        if (ctx.percorsoId) constraints.push(where('percorsoId', '==', ctx.percorsoId));

        const candidates = [];

        try {
          const targetedSnap = await getDocs(
            query(collection(db, QUESTIONARI_COLLECTION), ...constraints, orderBy('updatedAt', 'desc'))
          );
          targetedSnap.forEach((docSnap) => {
            candidates.push({ docSnap, data: docSnap.data() || {} });
          });
        } catch (err) {
          if (err?.code === 'failed-precondition') {
            const fallbackSnap = await getDocs(query(collection(db, QUESTIONARI_COLLECTION), ...constraints));
            fallbackSnap.forEach((docSnap) => {
              candidates.push({ docSnap, data: docSnap.data() || {} });
            });
          } else {
            throw err;
          }
        }

        if (candidates.length) {
          candidates.sort((a, b) => {
            const aTime = (toDateSafe(a.data.updatedAt || a.data.createdAt) || new Date(0)).getTime();
            const bTime = (toDateSafe(b.data.updatedAt || b.data.createdAt) || new Date(0)).getTime();
            return bTime - aTime;
          });

          const latest = candidates[0].data;
          initialQuestionarioAnswers.value = latest.answers || {};
          initialQuestionarioInfo.value = {
            createdAt: toDateSafe(latest.createdAt) || null,
            updatedAt: toDateSafe(latest.updatedAt || latest.createdAt) || null,
            templateLabel:
              latest.templateLabel ||
              latest.questionarioTemplateLabel ||
              latest.questionarioTemplate ||
              'Questionario Programmi',
          };
        } else {
          initialQuestionarioAnswers.value = {};
          initialQuestionarioInfo.value = { createdAt: null, updatedAt: null, templateLabel: null };
        }
      } catch (error) {
        console.error('Errore caricando il questionario iniziale:', error);
        initialQuestionarioAnswers.value = {};
        initialQuestionarioInfo.value = { createdAt: null, updatedAt: null, templateLabel: null };
        initialError.value = 'Impossibile recuperare il questionario iniziale.';
      } finally {
        initialLoading.value = false;
      }
    };

    watch(
      form,
      () => {
        if (!suppressDirty.value) dirty.value = true;
      },
      { deep: true }
    );

    watch(
      () => form.integrazioneAssunta,
      (value) => {
        if (value !== 'Si') form.integrazioneDettagli = '';
      }
    );

    watch(
      () => form.recuperoSonnoContinuativo,
      (value) => {
        if (value !== 'No') form.recuperoSonnoMotivo = '';
      }
    );

    watch(activeSectionIndex, () => scrollToActiveSection());

    watch(allowPartialSubmission, () => {
      if (allowPartialSubmission.value) {
        missingMandatoryFields.value = [];
      } else if (missingMandatoryFields.value.length) {
        missingMandatoryFields.value = validateMandatoryFields();
      }
    });

    const buildQuestionarioMetadata = () => {
      const ctx = percorsoContext.value;
      return {
        percorsoId: ctx.percorsoId || null,
        stepIndex: Number.isInteger(ctx.stepIndex) ? ctx.stepIndex : null,
        stepKey: ctx.stepKey || null,
        sequenceGroupId: ctx.sequenceGroupId || null,
        sequenceIndex: Number.isInteger(ctx.sequenceIndex) ? ctx.sequenceIndex : null,
        sequenceTotal: Number.isInteger(ctx.sequenceTotal) ? ctx.sequenceTotal : null,
        productId: ctx.productId || null,
        productName: ctx.productName || null,
        programTitle: ctx.programTitle || ctx.programLabel || null,
        programLabel: ctx.programLabel || ctx.programTitle || null,
        plannedStartAt: ctx.plannedStartAt || null,
        expireAt: ctx.expireAt || null,
        stepName: ctx.stepName || null,
      };
    };

    const buildQuestionarioUpdatePayload = (extra = {}) => ({
      ...buildQuestionarioMetadata(),
      questionarioUid: questionarioDocId.value || null,
      adminStatus: null,
      updatedAt: serverTimestamp(),
      ...extra,
    });

    const clearAdminStatusOnStep = async () => {
      if (!adminNeedsUpdate.value) return;
      const ctx = percorsoContext.value;
      if (!ctx.percorsoId || !Number.isInteger(ctx.stepIndex)) return;
      try {
        const percorsoRef = doc(db, 'percorsi', ctx.percorsoId);
        const snap = await getDoc(percorsoRef);
        if (!snap.exists()) return;
        const data = snap.data() || {};
        const steps = Array.isArray(data.steps) ? [...data.steps] : [];
        const idx = ctx.stepIndex;
        if (!steps[idx]) return;
        steps[idx] = { ...steps[idx], adminStatus: 'updated' };
        await updateDoc(percorsoRef, { steps });
        adminNeedsUpdate.value = false;
        submittedLock.value = stepDoneFlag.value;
      } catch (err) {
        console.warn('clearAdminStatusOnStep error', err);
      }
    };

    const syncQuestionarioStepMetadata = async (docId, overrides = {}) => {
      const ctx = percorsoContext.value;
      if (!docId || !ctx.percorsoId || ctx.stepIndex == null || ctx.stepIndex < 0) return;
      try {
        const percorsoRef = doc(db, 'percorsi', ctx.percorsoId);
        const snap = await getDoc(percorsoRef);
        if (!snap.exists()) return;
        const data = snap.data() || {};
        const steps = Array.isArray(data.steps) ? [...data.steps] : [];
        const idx = ctx.stepIndex;
        const prev = steps[idx] || {};
        const prevQuestionnaire = prev.questionnaire || {};
        const mergedMeta = {
          ...prevQuestionnaire,
          ...buildQuestionarioMetadata(),
          ...overrides,
          id: docId,
          type: QUESTIONARIO_TYPE,
          questionarioUid: docId,
        };

        if (!mergedMeta.questionarioKey) {
          const qKey =
            mergedMeta.percorsoId && Number.isInteger(mergedMeta.stepIndex)
              ? `${mergedMeta.percorsoId}__${mergedMeta.stepIndex}`
              : mergedMeta.percorsoId && mergedMeta.stepKey
              ? `${mergedMeta.percorsoId}__${mergedMeta.stepKey}`
              : null;
          mergedMeta.questionarioKey = qKey;
        }

        if (overrides.updatedAt) {
          mergedMeta.updatedAt = overrides.updatedAt instanceof Date ? overrides.updatedAt.toISOString() : overrides.updatedAt;
        }

        if (overrides.createdAt) {
          mergedMeta.createdAt = overrides.createdAt instanceof Date ? overrides.createdAt.toISOString() : overrides.createdAt;
        } else if (!mergedMeta.createdAt && questionarioInfo.value.createdAt) {
          mergedMeta.createdAt = questionarioInfo.value.createdAt.toISOString();
        }

        mergedMeta.templateId = overrides.templateId || mergedMeta.templateId || QUESTIONARIO_TEMPLATE.id;
        mergedMeta.templateKey = overrides.templateKey || mergedMeta.templateKey || QUESTIONARIO_TEMPLATE.key;
        mergedMeta.templateLabel = overrides.templateLabel || mergedMeta.templateLabel || QUESTIONARIO_TEMPLATE.label;
        mergedMeta.templateVersion = overrides.templateVersion ?? mergedMeta.templateVersion ?? QUESTIONARIO_TEMPLATE.version;

        steps[idx] = {
          ...prev,
          questionarioDocId: docId,
          questionnaire: mergedMeta,
        };

        await updateDoc(percorsoRef, { steps });
      } catch (error) {
        console.error('Errore aggiornando metadati check finale nel percorso:', error);
      }
    };

    const maybeBackfillQuestionarioDoc = async (docId, data = {}) => {
      if (!docId) return;
      const metadata = buildQuestionarioMetadata();
      const patch = {};
      let needsPatch = false;

      Object.entries(metadata).forEach(([key, value]) => {
        if (value == null) return;
        const existing = data[key];
        if (existing == null || (typeof existing === 'string' && !existing.trim())) {
          patch[key] = value;
          needsPatch = true;
        }
      });

      if (!data.questionarioUid) {
        patch.questionarioUid = docId;
        needsPatch = true;
      }

      if (needsPatch) {
        try {
          await updateDoc(doc(db, QUESTIONARI_COLLECTION, docId), patch);
        } catch (err) {
          console.warn('Impossibile aggiornare i metadati del check finale:', err);
        }
      }
    };

    const ensureQuestionarioDoc = async () => {
      if (isAdminApproved.value) {
        showApprovedLockMessage();
        throw new Error('Check già approvato.');
      }
      if (submittedLock.value) {
        showActionMessage(submittedLockMessage, 'error');
        if (typeof window !== 'undefined') {
          window.alert(submittedLockMessage);
        }
        throw new Error('Check già inviato.');
      }
      if (questionarioDocId.value) return questionarioDocId.value;
      const uid = effectiveUserId.value;
      if (!uid) throw new Error('ID utente non disponibile.');
      const docRef = doc(collection(db, QUESTIONARI_COLLECTION));
      const metadata = buildQuestionarioMetadata();
      await setDoc(docRef, {
        uid,
        type: QUESTIONARIO_TYPE,
        templateId: QUESTIONARIO_TEMPLATE.id,
        templateKey: QUESTIONARIO_TEMPLATE.key,
        templateLabel: QUESTIONARIO_TEMPLATE.label,
        templateVersion: QUESTIONARIO_TEMPLATE.version,
        answers: plainAnswers(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        ...metadata,
        ...buildInitialSnapshotPayload(),
        questionarioUid: docRef.id,
      });
      const now = new Date();
      questionarioDocId.value = docRef.id;
      loadedDocUid.value = uid;
      questionarioInfo.value = { createdAt: now, updatedAt: now, templateLabel: QUESTIONARIO_TEMPLATE.label };
      allowPartialSubmission.value = false;
      await syncQuestionarioStepMetadata(docRef.id, {
        createdAt: now,
        updatedAt: now,
      });
      return docRef.id;
    };

    const loadQuestionario = async () => {
      let uid = effectiveUserId.value;
      loading.value = true;
      errorMessage.value = '';
      lockWarning.value = '';
      submittedLock.value = false;
      clearDocContext();
      successMessage.value = '';
      manualStandalone.value = isTruthyManualStandalone(pickQueryValue('manualStandalone', route));

      const tryLoadPreferredDoc = async () => {
        if (!preferredDocId.value) return false;
        try {
          const snap = await getDoc(doc(db, QUESTIONARI_COLLECTION, preferredDocId.value));
          if (!snap.exists()) return false;
          const data = snap.data() || {};
          if (data.type !== QUESTIONARIO_TYPE) return false;
          if (data.uid && currentUserId.value !== data.uid) currentUserId.value = data.uid;
          loadedDocUid.value = data.uid || '';
          questionarioDocId.value = snap.id;
          setDocContext(data);
          setFormData(data.answers || {}, data);
          allowPartialSubmission.value = !!data.allowPartial;
          manualStandalone.value = isTruthyManualStandalone(data.manualStandalone) || manualStandalone.value;
          await maybeBackfillQuestionarioDoc(snap.id, data);
          await syncQuestionarioStepMetadata(snap.id, {
            templateId: data.templateId || QUESTIONARIO_TEMPLATE.id,
            templateKey: data.templateKey || data.questionarioTemplateKey || QUESTIONARIO_TEMPLATE.key,
            templateLabel: data.templateLabel || QUESTIONARIO_TEMPLATE.label,
            templateVersion: data.templateVersion ?? QUESTIONARIO_TEMPLATE.version,
            createdAt: toDateSafe(data.createdAt) || undefined,
            updatedAt: toDateSafe(data.updatedAt || data.createdAt) || undefined,
            questionarioUid: data.questionarioUid || snap.id,
          });
          await loadInitialQuestionario();
          return true;
        } catch (err) {
          console.warn('Impossibile caricare il check finale preferito:', err);
          return false;
        }
      };

      const loadedPreferred = await tryLoadPreferredDoc();
      if (loadedPreferred) {
        await evaluatePercorsoLock();
        if (lockWarning.value) {
          loading.value = false;
          return;
        }
        loading.value = false;
        return;
      }

      if (!uid) {
        loading.value = false;
        return;
      }

      await evaluatePercorsoLock();
      if (lockWarning.value) {
        loading.value = false;
        return;
      }

      try {
        const ctx = percorsoContext.value;
        const candidateMap = new Map();

        if (ctx.percorsoId && ctx.stepKey) {
          const constraints = [
            where('uid', '==', uid),
            where('type', '==', QUESTIONARIO_TYPE),
            where('percorsoId', '==', ctx.percorsoId),
            where('stepKey', '==', ctx.stepKey),
          ];
          if (Number.isInteger(ctx.stepIndex)) {
            constraints.push(where('stepIndex', '==', ctx.stepIndex));
          }
          const targetedSnap = await getDocs(query(collection(db, QUESTIONARI_COLLECTION), ...constraints));
          targetedSnap.forEach((docSnap) => {
            candidateMap.set(docSnap.id, docSnap);
          });
        }

        if (!candidateMap.size) {
          try {
            const orderedSnap = await getDocs(
              query(
                collection(db, QUESTIONARI_COLLECTION),
                where('uid', '==', uid),
                where('type', '==', QUESTIONARIO_TYPE),
                orderBy('updatedAt', 'desc')
              )
            );
            orderedSnap.forEach((docSnap) => candidateMap.set(docSnap.id, docSnap));
          } catch (err) {
            if (err?.code === 'failed-precondition') {
              const fallbackSnap = await getDocs(
                query(
                  collection(db, QUESTIONARI_COLLECTION),
                  where('uid', '==', uid),
                  where('type', '==', QUESTIONARIO_TYPE)
                )
              );
              fallbackSnap.forEach((docSnap) => candidateMap.set(docSnap.id, docSnap));
            } else {
              throw err;
            }
          }
        }

        const candidateDocs = Array.from(candidateMap.values()).map((docSnap) => {
          const data = docSnap.data() || {};
          const updated = toDateSafe(data.updatedAt || data.createdAt);
          return { docSnap, data, sortValue: updated ? updated.getTime() : 0 };
        });

        candidateDocs.sort((a, b) => b.sortValue - a.sortValue);

        const ctxForSelection = ctx;
        const wantSpecificStep = !!(ctxForSelection.percorsoId && ctxForSelection.stepKey);
        const wantSpecificIndex = Number.isInteger(ctxForSelection.stepIndex);
        const pickCandidate = () => {
          if (!candidateDocs.length) return null;
          if (!wantSpecificStep) return candidateDocs[0];

          const strictMatch = candidateDocs.find(({ data }) => {
            const meta = data || {};
            if (meta.percorsoId && ctxForSelection.percorsoId && meta.percorsoId !== ctxForSelection.percorsoId) return false;
            if (meta.stepKey && meta.stepKey !== ctxForSelection.stepKey) return false;
            if (wantSpecificIndex) {
              const docIndex = toStepIndex(meta.stepIndex);
              return docIndex === ctxForSelection.stepIndex;
            }
            return true;
          });
          if (strictMatch) return strictMatch;

          if (!wantSpecificIndex) {
            return candidateDocs.find(({ data }) => {
              const meta = data || {};
              if (meta.percorsoId && ctxForSelection.percorsoId && meta.percorsoId !== ctxForSelection.percorsoId) return false;
              if (meta.stepKey && meta.stepKey !== ctxForSelection.stepKey) return false;
              return true;
            }) || null;
          }

          return null;
        };

        const selected = pickCandidate();

        if (selected) {
          const { docSnap, data } = selected;
          questionarioDocId.value = docSnap.id;
          loadedDocUid.value = data.uid || '';
          setDocContext(data);
          setFormData(data.answers || {}, data);
          allowPartialSubmission.value = !!data.allowPartial;
          manualStandalone.value = isTruthyManualStandalone(data.manualStandalone) || manualStandalone.value;
          await maybeBackfillQuestionarioDoc(docSnap.id, data);
          await syncQuestionarioStepMetadata(docSnap.id, {
            templateId: data.templateId || QUESTIONARIO_TEMPLATE.id,
            templateKey: data.templateKey || data.questionarioTemplateKey || QUESTIONARIO_TEMPLATE.key,
            templateLabel: data.templateLabel || QUESTIONARIO_TEMPLATE.label,
            templateVersion: data.templateVersion ?? QUESTIONARIO_TEMPLATE.version,
            createdAt: toDateSafe(data.createdAt) || undefined,
            updatedAt: toDateSafe(data.updatedAt || data.createdAt) || undefined,
            questionarioUid: data.questionarioUid || docSnap.id,
          });
          const ctx = percorsoContext.value;
          if (!ctx.percorsoId || !Number.isInteger(ctx.stepIndex)) {
            await evaluatePercorsoLock();
          }
        } else {
          questionarioDocId.value = '';
          loadedDocUid.value = '';
          clearDocContext();
          setFormData({}, null);
          allowPartialSubmission.value = false;
          manualStandalone.value = isTruthyManualStandalone(pickQueryValue('manualStandalone', route));
          submittedLock.value = false;
          adminNeedsUpdate.value = false;
          stepDoneFlag.value = false;
        }
      } catch (error) {
        console.error('Errore caricando il check finale:', error);
        errorMessage.value = 'Impossibile recuperare il check finale.';
        questionarioDocId.value = '';
        loadedDocUid.value = '';
        clearDocContext();
        setFormData({}, null);
        allowPartialSubmission.value = false;
        manualStandalone.value = isTruthyManualStandalone(pickQueryValue('manualStandalone', route));
        submittedLock.value = false;
        adminNeedsUpdate.value = false;
        stepDoneFlag.value = false;
      } finally {
        await loadInitialQuestionario();
        loading.value = false;
      }
    };

    const saveQuestionario = async () => {
      if (saving.value || loading.value) return false;
      if (isAdminApproved.value) {
        showApprovedLockMessage();
        return false;
      }
      if (isHardLocked.value) {
        showActionMessage(submittedLockMessage, 'error');
        if (typeof window !== 'undefined') {
          window.alert(submittedLockMessage);
        }
        return false;
      }
      const uid = effectiveUserId.value;
      if (!uid) {
        showActionMessage('Devi essere autenticata per salvare il check finale.', 'error');
        return false;
      }
      if (Object.values(uploadingPhoto).some(Boolean)) {
        showActionMessage('Attendi il completamento del caricamento delle foto prima di salvare o inviare.', 'error');
        return false;
      }

      saving.value = true;
      showActionMessage('');
      successMessage.value = '';

      try {
        const savedAnswers = plainAnswers();
        const docId = await ensureQuestionarioDoc();
        const docRef = doc(db, QUESTIONARI_COLLECTION, docId);
        await updateDoc(docRef, {
          answers: savedAnswers,
          templateVersion: QUESTIONARIO_TEMPLATE.version,
          templateLabel: QUESTIONARIO_TEMPLATE.label,
          updatedAt: serverTimestamp(),
          questionarioUid: docId,
          adminStatus: null,
          ...buildInitialSnapshotPayload(),
          ...buildQuestionarioMetadata(),
        });

        const now = new Date();
        questionarioInfo.value = {
          createdAt: questionarioInfo.value.createdAt || now,
          updatedAt: now,
          templateLabel: QUESTIONARIO_TEMPLATE.label,
        };
        await syncQuestionarioStepMetadata(docId, { updatedAt: now, adminStatus: null });

        await clearAdminStatusOnStep();
        lastLoaded.value = deepClone(savedAnswers);
        dirty.value = JSON.stringify(plainAnswers()) !== JSON.stringify(savedAnswers);

        successMessage.value = isManualStandalone.value
          ? 'Il check è stato salvato correttamente e verrà messo in lavorazione.'
          : 'Il check è stato salvato correttamente';
        if (successTimer) clearTimeout(successTimer);
        successTimer = setTimeout(() => {
          successMessage.value = '';
        }, 4000);
        missingMandatoryFields.value = [];
        return true;
      } catch (error) {
        console.error('Errore nel salvataggio del check finale:', error);
        showActionMessage('Errore durante il salvataggio del check finale. Le risposte restano disponibili: riprova.', 'error');
        return false;
      } finally {
        saving.value = false;
      }
    };

    const resetForm = () => {
      suppressDirty.value = true;
      Object.assign(form, deepClone(lastLoaded.value));
      suppressDirty.value = false;
      dirty.value = false;
      errorMessage.value = '';
      successMessage.value = '';
      missingMandatoryFields.value = [];
    };

    const markStepCompleted = async () => {
      if (completingStep.value || saving.value || loading.value) return;
      if (Object.values(uploadingPhoto).some(Boolean)) {
        showActionMessage('Attendi il completamento del caricamento delle foto prima di inviare.', 'error');
        return;
      }
      if (isManualStandalone.value) return;
      if (isAdminApproved.value) {
        showApprovedLockMessage();
        return;
      }
      if (isHardLocked.value) {
        showActionMessage(submittedLockMessage, 'error');
        if (typeof window !== 'undefined') {
          window.alert(submittedLockMessage);
        }
        return;
      }
      const ctx = percorsoContext.value;
      if (!ctx.percorsoId || !Number.isInteger(ctx.stepIndex)) {
        showActionMessage('Impossibile determinare lo step da completare.', 'error');
        return;
      }
      const validationErrors = validateMandatoryFields();
      missingMandatoryFields.value = validationErrors;
      if (validationErrors.length) {
        showActionMessage('Completa foto e misurazioni obbligatorie prima di inviare il check.', 'error');
        alertMissingFields(validationErrors);
        return;
      }
      try {
        completingStep.value = true;
        if (dirty.value || !questionarioDocId.value) {
          const saved = await saveQuestionario();
          if (!saved) return;
        }
        if (dirty.value) {
          showActionMessage('Hai modificato alcune risposte durante il salvataggio. Controllale e premi di nuovo Invia.', 'error');
          return;
        }
        if (!questionarioDocId.value) {
          showActionMessage('Salva prima il check finale per poterlo completare.', 'error');
          return;
        }
        await setStepDone(ctx.percorsoId, ctx.stepIndex, true);
        showActionMessage('Step segnato come completato.');
        stepDoneFlag.value = true;
        adminNeedsUpdate.value = false;
        submittedLock.value = true;
        await clearAdminStatusOnStep();
        missingMandatoryFields.value = [];
      } catch (error) {
        console.error('markStepCompleted error', error);
        showActionMessage('Errore durante il completamento dello step.', 'error');
      } finally {
        completingStep.value = false;
      }
    };

    const uploadSingleFile = async (segments, file) => {
      const uid = effectiveUserId.value;
      if (isAdminApproved.value) {
        const message = 'Check già approvato';
        showApprovedLockMessage();
        throw Object.assign(new Error(message), { userMessage: message });
      }
      if (isHardLocked.value) {
        const message =
          'Check già inviato: contatta il supporto per riaprire il caricamento dei file.';
        showActionMessage(message, 'error');
        if (typeof window !== 'undefined') {
          window.alert(submittedLockMessage);
        }
        throw Object.assign(new Error(message), { userMessage: message });
      }
      if (!uid) {
        const message = 'Sessione scaduta: effettua di nuovo il login per caricare file e foto.';
        showActionMessage(message, 'error');
        throw Object.assign(new Error(message), { userMessage: message });
      }
      const docId = await ensureQuestionarioDoc();
      const fileName = `${Date.now()}_${sanitizeName(file.name)}`;
      const path = ['documenti', uid, 'questionari', docId, ...segments, fileName].join('/');
      const ref = storageRef(storage, path);
      await uploadBytesResumable(ref, file);
      const url = await getDownloadURL(ref);
      return { name: file.name, size: file.size, contentType: file.type || null, path, url };
    };

    const onPhotoChange = async (fieldKey, event) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;
      setUploadState(uploadingPhoto, fieldKey, true);
      try {
        const metadata = await uploadSingleFile(['foto', fieldKey], file);
        suppressDirty.value = true;
        form.foto[fieldKey] = metadata;
        suppressDirty.value = false;
        await updateDoc(
          doc(db, QUESTIONARI_COLLECTION, questionarioDocId.value),
          buildQuestionarioUpdatePayload({ answers: plainAnswers() })
        );
        lastLoaded.value = deepClone(form);
        await syncQuestionarioStepMetadata(questionarioDocId.value, { updatedAt: new Date() });
      } catch (error) {
        console.error('Errore caricando la foto:', error);
        showActionMessage(error.userMessage || 'Errore durante il caricamento della foto. Riprova.', 'error');
      } finally {
        setUploadState(uploadingPhoto, fieldKey, false);
      }
    };

    const removePhoto = async (fieldKey) => {
      const metadata = form.foto[fieldKey];
      if (!metadata) return;
      if (isAdminApproved.value) {
        showApprovedLockMessage();
        return;
      }
      if (isHardLocked.value) {
        showActionMessage(submittedLockMessage, 'error');
        if (typeof window !== 'undefined') {
          window.alert(submittedLockMessage);
        }
        return;
      }
      if (typeof window !== 'undefined') {
        const confirmDelete = window.confirm(`Eliminare "${metadata.name}"?`);
        if (!confirmDelete) return;
      }
      try {
        await deleteObject(storageRef(storage, metadata.path));
        suppressDirty.value = true;
        form.foto[fieldKey] = null;
        suppressDirty.value = false;
        await updateDoc(
          doc(db, QUESTIONARI_COLLECTION, questionarioDocId.value),
          buildQuestionarioUpdatePayload({ answers: plainAnswers() })
        );
        lastLoaded.value = deepClone(form);
        await syncQuestionarioStepMetadata(questionarioDocId.value, { updatedAt: new Date() });
      } catch (error) {
        console.error('Errore eliminando la foto:', error);
        showActionMessage('Errore durante l\'eliminazione della foto. Riprova.', 'error');
      }
    };

    watch(questionarioTargetKey, (next, prev) => {
      if (prev !== undefined && next !== prev) {
        loadQuestionario();
      }
    });

    watch(
      () => currentUserId.value,
      (next, prev) => {
        if (next && next !== prev) {
          loadQuestionario();
        }
      }
    );

    onMounted(() => {
      detachAuthListener = onAuthStateChanged(auth, (user) => {
        const nextUid = user?.uid || '';
        const prevUid = currentUserId.value;
        if (nextUid !== prevUid) {
          currentUserId.value = nextUid;
        }
        authResolved.value = true;
        if (nextUid) {
          loadQuestionario();
        } else {
          loading.value = false;
        }
      });

      scrollToActiveSection();
    });

    onBeforeUnmount(() => {
      if (successTimer) clearTimeout(successTimer);
      if (actionTimer) clearTimeout(actionTimer);
      if (typeof detachAuthListener === 'function') {
        detachAuthListener();
      }
    });

    return {
      form,
      loading,
      saving,
      dirty,
      errorMessage,
      lockWarning,
      successMessage,
      questionarioInfo,
      allowPartialSubmission,
      missingMandatoryFields,
      goBack,
      actionMessage,
      actionMessageType,
      exportFormAsPdf,
      pdfExporting,
      completingStep,
      canCompleteStep,
      showCompleteButton,
      uploadingPhoto,
      isImageFile,
      effectiveUserId,
      isFieldRequired,
      SEDUTE_SETTIMANALI_OPTIONS,
      ADESIONE_PERCENT_OPTIONS,
      STRATEGIA_ALIMENTARE_OPTIONS,
      RISPETTO_GRAMMATURE_OPTIONS,
      PASTI_LIBERI_OPTIONS,
      ACQUA_MEDIA_OPTIONS,
      ORE_SONNO_OPTIONS,
      TREND_SONNO_OPTIONS,
      YES_NO_OPTIONS,
      formatDate,
      initialQuestionarioInfo,
      initialLoading,
      initialError,
      initialSearch,
      checkSearch,
      groupedInitialEntries,
      groupedCheckEntries,
      progressValue,
      sectionFlow,
      activeSectionIndex,
      isFirstSection,
      isLastSection,
      nextSectionTitle,
      isSectionActive,
      goToSection,
      goToNextSection,
      handleFormSubmit,
      stepHeadingRef,
      actionMessageRef,
      requiredSummaryRef,
      goToMissingField,
      currentSection,
      isManualStandalone,
      goToPreviousSection,
      formRootRef,
      prettifyKey,
      saveQuestionario,
      resetForm,
      onPhotoChange,
      removePhoto,
      markStepCompleted,
      adminNeedsUpdate,
      isAdminApproved,
      isHardLocked,
      linkMismatchWarning,
      loginRoute,
    };
  },
};
</script>

<style scoped src="./styles/ProgrammiUserForm.css"></style>
